/**
 * Analytics Types & Emitter
 * Provides guarded analytics event emission with safe fallbacks
 * Task: 112-121-T003
 */

/**
 * ROI block analytics event payload on mount
 */
export interface ROIBlockRenderedEvent {
  event_name: 'roi_block_rendered'
  product: string // Product name (e.g., "Review", "Verify", "Insight")
  metrics_count: number // Number of metrics in the block
  has_substantiation: boolean // Whether substantiation links present
  timestamp: string // ISO 8601 timestamp
}

/**
 * Substantiation link click event payload
 */
export interface SubstantiationLinkClickedEvent {
  event_name: 'substantiation_link_clicked'
  link_title: string // Link display text
  link_url: string // Link target URL
  link_type: 'internal' | 'external' // Link type classification
  product: string // Product context (e.g., "Review", "Verify", "Insight")
  timestamp: string // ISO 8601 timestamp
}

/**
 * Pricing block rendered event payload
 */
export interface PricingBlockRenderedEvent {
  event_name: 'pricing_block_rendered'
  product: string // Product name (e.g., "Review", "Verify", "Insight")
  takeaways_count: number // Number of takeaways displayed
  has_cta: boolean // Whether CTA button is present
  timestamp: string // ISO 8601 timestamp
}

/**
 * Pricing CTA clicked event payload
 */
export interface PricingCTAClickedEvent {
  event_name: 'pricing_cta_clicked'
  cta_text: string // Button text label
  cta_url: string // Target URL
  link_type: 'internal' | 'external' // Link type classification
  product: string // Product context
  timestamp: string // ISO 8601 timestamp
}

/**
 * BookingsCTA clicked event payload (from story 112-141)
 */
export interface CTAClickedEvent {
  event_name: 'cta_clicked'
  location: string // CTA location (hero, mid, footer)
  product: string // Product context (review, verify, insight)
  button_text: string // Button text
  destination: string // Destination type (e.g., 'bookings', 'form')
  timestamp: string // ISO 8601 timestamp
}

/**
 * Page view event payload (from story 113-111)
 */
export interface PageViewEvent {
  event_name: 'page_view'
  page_path: string // e.g., '/contact/'
  page_title: string // e.g., 'Book an Appointment | LuminAI'
  timestamp: string // ISO 8601
}

/**
 * Bookings embed status event payload (from story 113-111)
 */
export interface BookingsEmbedStatusEvent {
  event_name: 'bookings_embed_status'
  status: 'loaded' | 'failed' | 'timeout'
  timestamp: string // ISO 8601
}

/**
 * Bookings fallback triggered event payload (from story 113-111)
 */
export interface BookingsFallbackTriggeredEvent {
  event_name: 'bookings_fallback_triggered'
  reason: string // e.g., 'Booking embed timed out after 5 seconds'
  timestamp: string // ISO 8601
}

/**
 * Blog search submitted event payload (from story 115-122)
 */
export interface BlogSearchSubmittedEvent {
  event_name: 'blog_search_submitted'
  search_query: string
  result_count: number
  active_filters: { product?: string; category?: string } | null
  response_time_ms: number
  timestamp: string
}

/**
 * Blog search no results event payload (from story 115-122)
 */
export interface BlogSearchNoResultsEvent {
  event_name: 'blog_search_no_results'
  search_query: string
  active_filters: { product?: string; category?: string } | null
  timestamp: string
}

/**
 * Blog search result clicked event payload (from story 115-122)
 */
export interface BlogSearchResultClickedEvent {
  event_name: 'blog_search_result_clicked'
  search_query: string
  clicked_article_id: string
  result_position: number
  timestamp: string
}

export interface BlogListingViewedEvent {
  event_name: 'blog_listing_viewed'
  page_path: string
  page_title: string
  article_count: number
  current_page: number
  timestamp: string
}

export interface BlogArticleViewedEvent {
  event_name: 'blog_article_viewed'
  page_path: string
  article_slug: string
  article_title: string
  categories: string[]
  reading_time_minutes: number
  timestamp: string
}

export interface BlogArticleClickedEvent {
  event_name: 'blog_article_clicked'
  article_path: string
  article_slug: string
  article_title: string
  article_position: number
  current_page: number
  timestamp: string
}

export interface BlogPaginationClickedEvent {
  event_name: 'blog_pagination_clicked'
  page_path: string
  from_page: number
  to_page: number
  total_pages: number
  timestamp: string
}

export interface BlogScrollDepthEvent {
  event_name: 'blog_scroll_depth'
  page_path: string
  page_type: 'listing' | 'article'
  threshold_percent: 50
  timestamp: string
}

export interface BlogReadTimeEvent {
  event_name: 'blog_read_time'
  page_path: string
  page_type: 'listing' | 'article'
  threshold_seconds: 90
  timestamp: string
}

export interface BlogFilterAppliedEvent {
  event_name: 'blog_filter_applied'
  active_filters: { product?: string | null; category?: string | null }
  timestamp: string
}

export interface BlogFilterClearedEvent {
  event_name: 'blog_filter_cleared'
  timestamp: string
}

export interface SchemaValidationPassedEvent {
  event_name: 'schema_validation_passed'
  page_type: 'home' | 'product' | 'blog'
  schema_type: 'organization' | 'product' | 'article' | 'faq' | 'breadcrumb'
  timestamp: string
}

export interface SchemaValidationFailedEvent {
  event_name: 'schema_validation_failed'
  page_type: 'home' | 'product' | 'blog'
  schema_type: 'organization' | 'product' | 'article' | 'faq' | 'breadcrumb'
  reason_codes: string[]
  timestamp: string
}

export interface MetaValidationPassedEvent {
  event_name: 'meta_validation_passed'
  page_type: 'home' | 'product' | 'blog' | 'resource'
  timestamp: string
}

export interface MetaValidationFailedEvent {
  event_name: 'meta_validation_failed'
  page_type: 'home' | 'product' | 'blog' | 'resource'
  missing_fields: string[]
  timestamp: string
}

/**
 * Form submitted event payload (from story 116-122)
 */
export interface FormSubmittedEvent {
  event_name: 'form_submitted'
  form_id: string // Unique form identifier (e.g., 'contact-fallback', 'newsletter')
  form_name: string // Human-readable name
  page_path: string // Page where the form is located
  timestamp: string // ISO 8601
}

/**
 * Resource downloaded event payload (from story 116-122)
 */
export interface ResourceDownloadedEvent {
  event_name: 'resource_downloaded'
  resource_name: string // Name of the resource
  resource_url: string // URL of the resource
  resource_type: string // e.g., 'pdf', 'csv', 'xlsx', 'zip'
  page_path: string // Page where the download was initiated
  timestamp: string // ISO 8601
}

/**
 * Purchase completed event payload (from story 116-122)
 */
export interface PurchaseCompletedEvent {
  event_name: 'purchase_completed'
  product: string // Product name (e.g., 'review', 'verify', 'insight')
  plan: string // Plan/tier selected (e.g., 'starter', 'pro', 'enterprise')
  value: number // Monetary value of the purchase
  currency: string // Currency code (e.g., 'USD', 'AUD')
  timestamp: string // ISO 8601
}

/**
 * API request telemetry event (from story 116-123)
 * Captures latency and status for outbound API calls.
 * url_pattern must never contain query parameters or path segments with PII.
 */
export interface ApiRequestEvent {
  event_name: 'api_request'
  method: string // HTTP method (GET, POST, etc.)
  url_pattern: string // Sanitized URL pattern (no PII, no query params)
  status_code: number // HTTP response status code
  duration_ms: number // Request duration in milliseconds
  success: boolean // Whether the request succeeded (2xx)
  correlation_id: string // End-to-end correlation ID
  timestamp: string // ISO 8601 timestamp
}

/**
 * API exception telemetry event (from story 116-123)
 * Captures server-side or network-layer exceptions on API calls.
 */
export interface ApiExceptionEvent {
  event_name: 'api_exception'
  error_type: string // Error class / type name (e.g. 'NetworkError')
  error_code?: string // Optional numeric or string error code
  severity: 'Sev1' | 'Sev2' | 'Sev3' // Severity classification
  component: string // Source component (e.g. 'contact-form')
  correlation_id: string // End-to-end correlation ID
  timestamp: string // ISO 8601 timestamp
}

/**
 * Client error telemetry event (from story 116-123)
 * Captures unhandled browser errors and promise rejections.
 * Sampled at 100% to ensure no client errors are lost.
 */
export interface ClientErrorEvent {
  event_name: 'client_error'
  error_type: string // Error class / type name (e.g. 'TypeError')
  error_code?: string // Optional error code
  severity: 'Sev1' | 'Sev2' | 'Sev3' // Severity classification
  component: string // Source file or component context
  correlation_id: string // End-to-end correlation ID
  timestamp: string // ISO 8601 timestamp
}

export interface AnalyticsConsentProvider {
  hasConsented?: (category: string) => boolean
}

export interface AnalyticsConsentWindow {
  Osano?: {
    cm?: AnalyticsConsentProvider
  }
  cookieconsent?: AnalyticsConsentProvider
}

/**
 * Booking completed event payload (from story 113-112)
 * Emitted when the thank-you page confirms a successful booking
 */
export interface BookingCompletedEvent {
  event_name: 'booking_completed'
  source: 'microsoft_bookings'
  page_path: string // e.g., '/contact/thank-you/'
  timestamp: string // ISO 8601
  booking_details: string | null // Optional booking detail string from URL param; null if absent
}

export type AnalyticsConsentState = 'granted' | 'denied' | 'unavailable'

/**
 * Reads analytics consent state from supported consent providers.
 * Provider priority is Osano first, then cookieconsent.
 * Returns 'unavailable' when no supported consent provider is present.
 * Called by safeEmitAnalytics to annotate and gate telemetry emission.
 * Also consumed by useAnalyticsConsent composable to avoid duplication.
 */
export function readConsentState(): AnalyticsConsentState {
  const consentWindow = globalThis as unknown as AnalyticsConsentWindow
  const osanoHasConsented = consentWindow.Osano?.cm?.hasConsented
  if (typeof osanoHasConsented === 'function') {
    return osanoHasConsented('analytics') ? 'granted' : 'denied'
  }

  const cookieConsentHasConsented = consentWindow.cookieconsent?.hasConsented
  if (typeof cookieConsentHasConsented === 'function') {
    return cookieConsentHasConsented('analytics') ? 'granted' : 'denied'
  }

  return 'unavailable'
}

/**
 * Essential telemetry is limited to operational reliability events (API request,
 * API exception, client error) that are required for monitoring and incident response.
 * All other analytics events are treated as non-essential and blocked without consent.
 */
function isEssentialTelemetryEvent(eventName: AnalyticsEvent['event_name']): boolean {
  return eventName === 'api_request' || eventName === 'api_exception' || eventName === 'client_error'
}

export type AnalyticsEvent =
  | ROIBlockRenderedEvent
  | SubstantiationLinkClickedEvent
  | PricingBlockRenderedEvent
  | PricingCTAClickedEvent
  | CTAClickedEvent
  | PageViewEvent
  | BookingsEmbedStatusEvent
  | BookingsFallbackTriggeredEvent
  | BlogSearchSubmittedEvent
  | BlogSearchNoResultsEvent
  | BlogSearchResultClickedEvent
  | BlogListingViewedEvent
  | BlogArticleViewedEvent
  | BlogArticleClickedEvent
  | BlogPaginationClickedEvent
  | BlogScrollDepthEvent
  | BlogReadTimeEvent
  | BlogFilterAppliedEvent
  | BlogFilterClearedEvent
  | FormSubmittedEvent
  | ResourceDownloadedEvent
  | PurchaseCompletedEvent
  | BookingCompletedEvent
  | MetaValidationPassedEvent
  | MetaValidationFailedEvent
  | SchemaValidationPassedEvent
  | SchemaValidationFailedEvent
  | ApiRequestEvent
  | ApiExceptionEvent
  | ClientErrorEvent

/**
 * Analytics client interface (minimal contract)
 * Allows flexibility for different analytics providers
 * Supports both custom clients (emit method) and Application Insights (trackEvent method)
 */
export interface AnalyticsClient {
  emit: (event: AnalyticsEvent) => void | Promise<void>
}

/**
 * Application Insights client interface (from @microsoft/applicationinsights-web)
 * Detected via trackEvent method signature
 */
export interface AppInsightsClient {
  trackEvent: (event: { name: string; properties?: Record<string, unknown> }) => void
}

type ConsentAnnotatedAnalyticsEvent = AnalyticsEvent & { consent_state: AnalyticsConsentState }

/**
 * Global analytics client injection key
 */
export const ANALYTICS_CLIENT_KEY = Symbol('analyticsClient')

/**
 * Safe emit wrapper with console fallback
 * Guards against null/undefined analytics client
 * Provides dev-mode debug logging
 * Supports both custom clients (emit) and Application Insights (trackEvent)
 *
 * @param event - Analytics event to emit
 * @param client - Optional analytics client instance (custom or App Insights)
 * @returns void
 */
export function safeEmitAnalytics(
  event: AnalyticsEvent,
  client?: AnalyticsClient | AppInsightsClient | null
): void {
  const consentState = readConsentState()
  const eventWithConsent: ConsentAnnotatedAnalyticsEvent = {
    ...event,
    consent_state: consentState,
  }

  if (!client) {
    // Development fallback: log to console
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Analytics - No Client]', eventWithConsent)
    }
    return
  }

  if (consentState !== 'granted' && !isEssentialTelemetryEvent(event.event_name)) {
    return
  }

  try {
    // Detect Application Insights client (has trackEvent method)
    if ('trackEvent' in client && typeof client.trackEvent === 'function') {
      // App Insights format: { name, properties }
      const appInsightsClient = client as AppInsightsClient
      appInsightsClient.trackEvent({
        name: event.event_name,
        properties: {
          ...eventWithConsent,
          timestamp: event.timestamp || getCurrentTimestamp(),
        },
      })
      return
    }

    // Otherwise, assume it's a custom client with emit method
    if ('emit' in client && typeof client.emit === 'function') {
      const customClient = client as AnalyticsClient
      customClient.emit(eventWithConsent)
      return
    }

    // Unknown client type
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Analytics] Unknown client type (no emit or trackEvent method)')
    }
  } catch (error) {
    // Guard: Prevent analytics errors from crashing app
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Analytics - Emit Error]', error)
    }
    // Silently fail in production
  }
}

/**
 * Helper to generate ISO 8601 timestamp
 * @returns ISO timestamp string
 */
export function getCurrentTimestamp(): string {
  return new Date().toISOString()
}
